import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import apiClient, { formatApiError, authedFileUrl } from "@/api/client";
import { ArrowLeft, Mail, Phone, MapPin, Building2, Globe, Briefcase, ClipboardList, FileText, LifeBuoy, Upload, Plus, Send, Trash2, Download, Eye, EyeOff, Sparkles, X, Loader2, Rocket } from "lucide-react";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { OnboardingDetail } from "@/pages/OnboardingPage";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { key: "overview", label: "Genel" },
  { key: "onboarding", label: "Onboarding", icon: Rocket },
  { key: "services", label: "Hizmetler" },
  { key: "domains", label: "Domainler" },
  { key: "tasks", label: "Görevler" },
  { key: "keywords", label: "Anahtar Kelimeler" },
  { key: "adcopy", label: "Reklam Metinleri" },
  { key: "reports", label: "Raporlar (PDF)" },
  { key: "files", label: "Dosyalar" },
  { key: "notes", label: "Notlar" },
  { key: "tickets", label: "Destek" },
  { key: "activity", label: "Aktivite" },
];

export default function CustomerDetailPage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const [uploadVis, setUploadVis] = useState(true);
  const [gmailOpen, setGmailOpen] = useState(false);
  const [gmailForm, setGmailForm] = useState({ to: "", subject: "", body: "", purpose: "" });
  const [gmailSending, setGmailSending] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [google, setGoogle] = useState({ connected: false });

  useEffect(() => { apiClient.get("/integrations/google/status").then(r=>setGoogle(r.data)).catch(()=>{}); }, []);

  const load = () => apiClient.get(`/customers/${cid}`).then(r => setData(r.data));
  useEffect(() => { load(); }, [cid]);

  const addNote = async () => {
    if (!note.trim()) return;
    await apiClient.post(`/customers/${cid}/notes`, { text: note });
    setNote(""); load();
  };

  const delNote = async (id) => { if (!window.confirm("Silinsin mi?")) return; await apiClient.delete(`/customers/${cid}/notes/${id}`); load(); };

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("customer_id", cid);
    fd.append("visible_to_customer", uploadVis ? "true" : "false");
    try {
      await apiClient.post("/files/upload", fd);
      load();
    } catch (e) {
      alert("Dosya yüklenemedi: " + (e?.response?.data?.detail || e.message));
    }
  };

  const delFile = async (id) => { if (!window.confirm("Silinsin mi?")) return; await apiClient.delete(`/files/${id}`); load(); };

  if (!data) return <div className="text-sm text-[#94A3B8]">Yükleniyor…</div>;
  const c = data.customer;

  return (
    <div className="space-y-5" data-testid="customer-detail-page">
      <button onClick={() => nav("/musteriler")} className="ky-btn ky-btn-ghost ky-btn-sm"><ArrowLeft size={12} /> Müşteriler</button>

      {/* Header card */}
      <div className="ky-card p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <Avatar name={c.name} size={56} />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold">{c.name}</h1>
            {c.company && <div className="text-sm text-[#64748B] flex items-center gap-1 mt-0.5"><Building2 size={12} /> {c.company} · {c.industry || "—"}</div>}
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-[#64748B]">
              {c.email && <span className="flex items-center gap-1"><Mail size={11} />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
              {c.address && <span className="flex items-center gap-1"><MapPin size={11} />{c.address}</span>}
              {c.tax_id && <span className="font-mono">VKN: {c.tax_id}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {c.portal_enabled && <span className="ky-badge ky-badge-success">Portal Aktif</span>}
            {c.email && <button onClick={()=>{ setGmailForm({ to: c.email, subject: "", body: "", purpose: "" }); setGmailOpen(true); }} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="open-gmail-compose"><Mail size={11}/>E-posta</button>}
          </div>
        </div>
        {/* Quick stats */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3 pt-5 border-t border-[#ECECEF]">
          <Stat label="Aktif Hizmet" value={data.services.filter(s => s.status === "aktif").length} />
          <Stat label="Tamamlanan" value={data.services.filter(s => s.status === "tamamlandi").length} />
          <Stat label="Domain" value={data.domains.length} />
          <Stat label="Açık Görev" value={data.tasks.filter(t => t.status !== "tamamlandi").length} />
          <Stat label="Açık Talep" value={data.tickets.filter(t => t.status !== "closed").length} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#ECECEF] overflow-x-auto" data-testid="detail-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition ${tab === t.key ? "border-[#4F46E5] text-[#4F46E5]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "onboarding" && (
        <OnboardingDetail customerId={cid}/>
      )}

      {tab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <SectionCard icon={Briefcase} title="Aktif Hizmetler" empty={data.services.filter(s=>s.status==='aktif').length===0}>
            {data.services.filter(s => s.status === "aktif").slice(0, 5).map(s => (
              <div key={s.id} className="flex items-center justify-between py-2 border-b border-[#ECECEF] last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-xs text-[#64748B] uppercase tracking-wider mt-0.5">{s.service_type}</div>
                </div>
                <div className="text-xs text-[#64748B]">{s.progress || 0}%</div>
              </div>
            ))}
          </SectionCard>
          <SectionCard icon={Globe} title="Domainler" empty={data.domains.length===0}>
            {data.domains.slice(0, 5).map(d => {
              const daysLeft = d.expiry_date ? Math.floor((new Date(d.expiry_date) - new Date()) / 86400000) : null;
              return (
                <div key={d.id} className="flex items-center justify-between py-2 border-b border-[#ECECEF] last:border-0">
                  <div className="font-mono text-sm">{d.domain}</div>
                  <div className={`ky-badge ${daysLeft < 0 ? "ky-badge-danger" : daysLeft < 15 ? "ky-badge-danger" : daysLeft < 30 ? "ky-badge-warning" : "ky-badge-neutral"}`}>{daysLeft !== null ? `${daysLeft}g` : "—"}</div>
                </div>
              );
            })}
          </SectionCard>
          <SectionCard icon={LifeBuoy} title="Son Destek Talepleri" empty={data.tickets.length===0}>
            {data.tickets.slice(0, 5).map(t => (
              <Link to={`/destek/${t.id}`} key={t.id} className="flex items-center justify-between py-2 border-b border-[#ECECEF] last:border-0 hover:bg-[#FAFAFB] px-1">
                <div className="text-sm truncate">{t.subject}</div>
                <span className={`ky-badge ${t.status === "open" ? "ky-badge-warning" : t.status === "closed" ? "ky-badge-neutral" : "ky-badge-info"}`}>{t.status}</span>
              </Link>
            ))}
          </SectionCard>
          <SectionCard icon={FileText} title="Son Notlar" empty={data.notes.length===0}>
            {data.notes.slice(0, 4).map(n => (
              <div key={n.id} className="py-2 border-b border-[#ECECEF] last:border-0">
                <div className="text-sm">{n.text}</div>
                <div className="text-[11px] text-[#94A3B8] mt-1">{n.author_name} · {new Date(n.created_at).toLocaleDateString("tr-TR")}</div>
              </div>
            ))}
          </SectionCard>
        </div>
      )}

      {tab === "services" && (
        <div className="grid gap-3">
          {data.services.map(s => (
            <div key={s.id} className="ky-card p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="text-sm font-semibold">{s.title}</span><span className="ky-badge ky-badge-indigo">{s.service_type}</span><span className={`ky-badge ${s.status === "aktif" ? "ky-badge-success" : s.status === "tamamlandi" ? "ky-badge-neutral" : "ky-badge-warning"}`}>{s.status}</span></div>
                {s.deadline && <div className="text-xs text-[#64748B] mt-1">Teslim: {new Date(s.deadline).toLocaleDateString("tr-TR")}</div>}
                <div className="h-1.5 bg-[#F4F4F5] rounded-full mt-2 overflow-hidden"><div className="h-full bg-[#4F46E5]" style={{ width: `${s.progress || 0}%` }} /></div>
              </div>
            </div>
          ))}
          {data.services.length === 0 && <Empty />}
        </div>
      )}

      {tab === "domains" && (
        <div className="ky-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFB] border-b border-[#ECECEF]">
              <tr><th className="text-left text-[11px] uppercase text-[#64748B] font-semibold px-4 py-2">Domain</th><th className="text-left text-[11px] uppercase text-[#64748B] font-semibold px-4 py-2">Bitiş</th><th className="text-left text-[11px] uppercase text-[#64748B] font-semibold px-4 py-2">Kayıt Eden</th></tr>
            </thead>
            <tbody>{data.domains.map(d => (<tr key={d.id} className="border-b border-[#ECECEF]"><td className="px-4 py-3 font-mono">{d.domain}</td><td className="px-4 py-3">{d.expiry_date ? new Date(d.expiry_date).toLocaleDateString("tr-TR") : "—"}</td><td className="px-4 py-3">{d.registrar || "—"}</td></tr>))}</tbody>
          </table>
          {data.domains.length === 0 && <div className="text-center py-12"><Empty /></div>}
        </div>
      )}

      {tab === "tasks" && (
        <div className="grid gap-2">
          {data.tasks.map(t => (
            <div key={t.id} className="ky-card p-3 flex items-center gap-3">
              <div className={`w-1.5 h-1.5 rounded-full ${t.priority === "kritik" ? "bg-[#EF4444]" : t.priority === "yuksek" ? "bg-[#F59E0B]" : "bg-[#94A3B8]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t.title}</div>
                <div className="text-[11px] text-[#94A3B8]">{t.status?.replace("_", " ")} · {t.due_date ? new Date(t.due_date).toLocaleDateString("tr-TR") : "—"}</div>
              </div>
              <span className="ky-badge ky-badge-neutral">{t.priority}</span>
            </div>
          ))}
          {data.tasks.length === 0 && <Empty />}
        </div>
      )}

      {tab === "files" && (
        <div className="space-y-3">
          <div className="ky-card p-4 flex flex-wrap items-center gap-3">
            <label className="ky-btn ky-btn-accent cursor-pointer"><Upload size={14} />Dosya Yükle<input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg,.heic,.heif,.bmp,.tiff,.zip" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="customer-file-upload" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={uploadVis} onChange={(e) => setUploadVis(e.target.checked)} /> Müşteri portalında görünür</label>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.files.map(f => (
              <div key={f.id} className="ky-card p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-[#EEF2FF] text-[#4F46E5] rounded-lg flex items-center justify-center"><FileText size={16} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.original_filename}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">{(f.size / 1024).toFixed(1)} KB · {f.uploaded_by_name}</div>
                  <div className="flex items-center gap-1 mt-2 text-[11px] text-[#64748B]">{f.visible_to_customer ? <><Eye size={10} className="text-[#10B981]" /> Müşteri görür</> : <><EyeOff size={10} /> Sadece dahili</>}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <a href={authedFileUrl(f.id)} target="_blank" rel="noreferrer" className="ky-btn ky-btn-ghost ky-btn-icon"><Download size={13} /></a>
                  <button onClick={() => delFile(f.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
            {data.files.length === 0 && <div className="col-span-3"><Empty /></div>}
          </div>
        </div>
      )}

      {tab === "notes" && (
        <div className="space-y-3">
          <div className="ky-card p-4">
            <div className="flex items-end gap-2">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Yeni not ekle…" className="ky-textarea flex-1" rows={2} data-testid="note-input" />
              <button onClick={addNote} className="ky-btn ky-btn-accent" data-testid="add-note-btn"><Plus size={14} />Ekle</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.notes.map(n => {
              const isPersonal = n.source === "personal";
              const content = isPersonal ? [n.title, n.body].filter(Boolean).join("\n") : n.text;
              return (
                <div key={n.id} className="ky-card p-4 flex gap-3" data-testid={`note-${n.id}`}>
                  <Avatar name={n.author_name} color={n.author_avatar_color} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{n.author_name}</span>
                        <span className="text-[11px] text-[#94A3B8]">{new Date(n.created_at).toLocaleString("tr-TR")}</span>
                        {isPersonal && <span className="ky-badge ky-badge-indigo text-[9px]">Kişisel Not</span>}
                      </div>
                      {!isPersonal && <button onClick={() => delNote(n.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={13} /></button>}
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{content}</div>
                  </div>
                </div>
              );
            })}
            {data.notes.length === 0 && <Empty />}
          </div>
        </div>
      )}

      {tab === "keywords" && (
        <CustomerKeywordsTab cid={cid}/>
      )}

      {tab === "adcopy" && (
        <CustomerAdCopyTab cid={cid}/>
      )}

      {tab === "reports" && (
        <CustomerReportsTab cid={cid}/>
      )}

      {tab === "tickets" && (
        <div className="space-y-2">
          {data.tickets.map(t => (
            <Link to={`/destek/${t.id}`} key={t.id} className="ky-card p-4 flex items-center gap-3 ky-card-hover">
              <LifeBuoy size={16} className="text-[#4F46E5]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{t.subject}</div>
                <div className="text-[11px] text-[#94A3B8]">Açılma: {new Date(t.created_at).toLocaleString("tr-TR")}</div>
              </div>
              <span className={`ky-badge ${t.status === "open" ? "ky-badge-warning" : "ky-badge-neutral"}`}>{t.status}</span>
            </Link>
          ))}
          {data.tickets.length === 0 && <Empty />}
        </div>
      )}

      {tab === "activity" && (
        <div className="ky-card p-5">
          <div className="space-y-3">
            {data.activity.map(a => (
              <div key={a.id} className="flex items-start gap-3 pb-3 border-b border-[#ECECEF] last:border-0">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] mt-2" />
                <div className="flex-1 text-sm">{a.text}</div>
                <div className="text-[11px] text-[#94A3B8]">{new Date(a.created_at).toLocaleString("tr-TR")}</div>
              </div>
            ))}
            {data.activity.length === 0 && <Empty />}
          </div>
        </div>
      )}

      {/* Gmail Compose Drawer */}
      {gmailOpen && (
        <div className="fixed inset-0 z-50 flex" onClick={()=>setGmailOpen(false)}>
          <div className="flex-1 bg-black/40"/>
          <div onClick={e=>e.stopPropagation()} className="w-full sm:w-[560px] bg-white flex flex-col ky-slide-in" data-testid="gmail-compose">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between">
              <div className="flex items-center gap-2"><Mail size={16} className="text-[#4F46E5]"/><h3 className="font-semibold">E-posta Yaz</h3></div>
              <button onClick={()=>setGmailOpen(false)} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {!google.connected && (
                <div className="text-xs px-3 py-2 rounded-md bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">
                  Google Workspace bağlı değil. Bağlamak için <Link to="/yonetim" className="underline font-semibold">Yönetim → Entegrasyonlar</Link>.
                </div>
              )}
              {google.connected && (
                <div className="text-xs px-3 py-2 rounded-md bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]">
                  ✉️ <strong>{google.email}</strong> hesabınızdan gönderilecek
                </div>
              )}

              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Kime</label><input value={gmailForm.to} onChange={e=>setGmailForm({...gmailForm,to:e.target.value})} className="ky-input" data-testid="gmail-to"/></div>

              {/* AI Draft */}
              <div className="ky-card p-3 bg-gradient-to-br from-[#EEF2FF] to-white border-[#C7D2FE]">
                <div className="flex items-center gap-2 mb-2"><Sparkles size={13} className="text-[#7C3AED]"/><span className="text-xs font-semibold">AI ile Taslak</span></div>
                <input value={gmailForm.purpose} onChange={e=>setGmailForm({...gmailForm,purpose:e.target.value})} placeholder="Amaç: 'haftalık ilerleme raporu', 'yeni teklif', 'fatura hatırlatma'…" className="ky-input h-8 text-sm" data-testid="gmail-ai-purpose"/>
                <button onClick={async ()=>{
                  if (!gmailForm.purpose.trim()) return;
                  setAiDrafting(true);
                  try {
                    const { data: r } = await apiClient.post("/ai/email-draft", { customer_id: cid, purpose: gmailForm.purpose });
                    setGmailForm(f => ({ ...f, subject: r.subject, body: r.body }));
                    toast.success("AI taslağı hazır");
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setAiDrafting(false); }
                }} disabled={aiDrafting} className="ky-btn ky-btn-accent ky-btn-sm w-full mt-2" data-testid="gmail-ai-generate">
                  {aiDrafting ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>} AI ile yaz
                </button>
              </div>

              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Konu</label><input value={gmailForm.subject} onChange={e=>setGmailForm({...gmailForm,subject:e.target.value})} className="ky-input" data-testid="gmail-subject"/></div>
              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Gövde</label><textarea value={gmailForm.body} onChange={e=>setGmailForm({...gmailForm,body:e.target.value})} className="ky-textarea font-mono text-sm" rows={12} data-testid="gmail-body"/></div>
            </div>
            <div className="px-5 py-4 border-t border-[#ECECEF] flex gap-2">
              <button onClick={()=>setGmailOpen(false)} className="ky-btn ky-btn-outline flex-1">İptal</button>
              <button disabled={!google.connected || gmailSending || !gmailForm.to || !gmailForm.subject} onClick={async ()=>{
                setGmailSending(true);
                try {
                  await apiClient.post("/integrations/google/gmail/send", { ...gmailForm, customer_id: cid });
                  toast.success("E-posta gönderildi");
                  setGmailOpen(false); load();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setGmailSending(false); }
              }} className="ky-btn ky-btn-accent flex-1 disabled:opacity-50" data-testid="gmail-send">
                {gmailSending ? <Loader2 size={11} className="animate-spin"/> : <Send size={11}/>}Gönder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">{label}</div><div className="text-xl font-semibold tabular-nums mt-1">{value}</div></div>;
}
function SectionCard({ icon: Icon, title, children, empty }) {
  return <div className="ky-card p-5"><div className="flex items-center gap-2 mb-3"><Icon size={14} className="text-[#4F46E5]" /><h3 className="font-semibold text-sm">{title}</h3></div>{empty ? <Empty /> : <div className="space-y-1">{children}</div>}</div>;
}
function Empty() { return <div className="text-sm text-[#94A3B8] text-center py-6">Kayıt yok.</div>; }


function CustomerKeywordsTab({ cid }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  React.useEffect(() => {
    apiClient.get("/keyword-lists", { params: { customer_id: cid } })
      .then(r => setLists(r.data || []))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, [cid]);
  const nav = useNavigate();
  if (loading) return <div className="ky-card p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={14} className="animate-spin inline mr-2"/>Yükleniyor…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Anahtar Kelime Listeleri</h3>
        <button onClick={()=>nav("/ai-araclar/anahtar-kelime/listeler")} className="ky-btn ky-btn-outline ky-btn-sm"><Plus size={11}/>Yeni Liste Oluştur</button>
      </div>
      {lists.length === 0 && <div className="ky-card p-10 text-center text-sm text-[#94A3B8]">Bu müşteri için kayıtlı liste yok. <button onClick={()=>nav("/ai-araclar/anahtar-kelime")} className="text-[#4F46E5] underline">Anahtar Kelime Aracı'ndan</button> analiz başlatıp seçimle listeye ekleyebilirsiniz.</div>}
      {lists.map(l => (
        <div key={l.id} className="ky-card overflow-hidden">
          <button onClick={()=>setOpen({...open, [l.id]: !open[l.id]})} className="w-full p-3 flex items-center gap-3 text-left hover:bg-[#FAFAFB]">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{l.name}</div>
              <div className="text-[11px] text-[#94A3B8]">{(l.positives||[]).length} pozitif · {(l.negatives||[]).length} negatif · güncellendi {new Date(l.updated_at).toLocaleDateString("tr-TR")}</div>
            </div>
            <button onClick={(e)=>{e.stopPropagation(); nav(`/ai-araclar/anahtar-kelime/listeler/${l.id}`);}} className="ky-btn ky-btn-outline ky-btn-sm">Düzenle</button>
          </button>
          {open[l.id] && (
            <div className="border-t border-[#ECECEF] p-3 space-y-3">
              {(l.positives||[]).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#16A34A] mb-1.5">Pozitif ({(l.positives||[]).length})</div>
                  <div className="flex flex-wrap gap-1">{(l.positives||[]).slice(0, 40).map((p,i) => <span key={i} className="px-1.5 py-0.5 rounded text-[11px] font-mono bg-[#F0FDF4] border border-[#BBF7D0] text-[#166534]">{p.keyword}</span>)}</div>
                </div>
              )}
              {(l.negatives||[]).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#EF4444] mb-1.5">Negatif ({(l.negatives||[]).length})</div>
                  <div className="flex flex-wrap gap-1">{(l.negatives||[]).slice(0, 40).map((p,i) => <span key={i} className="px-1.5 py-0.5 rounded text-[11px] font-mono bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B]">{p.keyword}</span>)}</div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CustomerReportsTab({ cid }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();
  const { token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = () => apiClient.get("/client-reports", { params: { customer_id: cid } })
    .then(r => setReports(r.data || [])).catch(()=>{}).finally(()=>setLoading(false));
  React.useEffect(() => { load(); }, [cid]); // eslint-disable-line

  const create = async () => {
    if (!name.trim()) return toast.error("Rapor adı gerekli");
    setCreating(true);
    try {
      const { data } = await apiClient.post("/client-reports", {
        customer_id: cid, name: name.trim(),
        subtitle: "", summary: "", keyword_list_ids: [], ad_copy_set_ids: [], custom_sections: [],
      });
      toast.success("Rapor oluşturuldu, düzenleme açılıyor…");
      nav(`/raporlar/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); setName(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await apiClient.delete(`/client-reports/${id}`); load(); toast.success("Silindi");
  };

  if (loading) return <div className="ky-card p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={14} className="animate-spin inline mr-2"/>Yükleniyor…</div>;
  return (
    <div className="space-y-3">
      <div className="ky-card p-4 space-y-2">
        <h3 className="font-semibold text-sm">Yeni PDF Rapor Oluştur</h3>
        <p className="text-[11px] text-[#64748B]">Müşteri için kayıtlı anahtar kelime listelerini ve reklam metinlerini birleştiren özelleştirilebilir PDF raporu oluşturun.</p>
        <div className="flex gap-2">
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Örn: Mayıs 2026 SEO Raporu" className="ky-input flex-1" data-testid="new-report-name"/>
          <button onClick={create} disabled={creating} className="ky-btn ky-btn-accent" data-testid="create-report">
            {creating ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>}Oluştur
          </button>
        </div>
      </div>

      {reports.length === 0 && <div className="ky-card p-10 text-center text-sm text-[#94A3B8]">Henüz rapor yok.</div>}
      {reports.map(r => (
        <div key={r.id} className="ky-card p-3 flex items-center gap-3" data-testid={`report-row-${r.id}`}>
          <div onClick={()=>nav(`/raporlar/${r.id}`)} className="flex-1 min-w-0 cursor-pointer">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-medium text-sm truncate">{r.name}</span>
              <span className={`ky-badge text-[10px] ${r.status==="published"?"ky-badge-success":"ky-badge-neutral"}`}>{r.status === "published" ? "Yayınlandı" : "Taslak"}</span>
              {r.email_sent_at && <span className="ky-badge ky-badge-success text-[10px]">✉ Gönderildi</span>}
            </div>
            <div className="text-[11px] text-[#94A3B8]">
              {(r.keyword_list_ids||[]).length} liste · {(r.ad_copy_set_ids||[]).length} reklam seti
              {r.published_at && ` · ${new Date(r.published_at).toLocaleString("tr-TR")}`}
            </div>
          </div>
          {r.status === "published" && (
            <a href={`${API}/api/client-reports/${r.id}/pdf?auth=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer" className="ky-btn ky-btn-outline ky-btn-sm">PDF</a>
          )}
          <button onClick={()=>nav(`/raporlar/${r.id}`)} className="ky-btn ky-btn-outline ky-btn-sm">Aç</button>
          <button onClick={()=>remove(r.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={12}/></button>
        </div>
      ))}
    </div>
  );
}




function CustomerAdCopyTab({ cid }) {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const nav = useNavigate();
  const load = () => apiClient.get("/ad-copy-sets", { params: { customer_id: cid } })
    .then(r => setSets(r.data || [])).catch(()=>{}).finally(()=>setLoading(false));
  React.useEffect(() => { load(); }, [cid]); // eslint-disable-line
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await apiClient.delete(`/ad-copy-sets/${id}`); load(); toast.success("Silindi");
  };
  if (loading) return <div className="ky-card p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={14} className="animate-spin inline mr-2"/>Yükleniyor…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Kayıtlı Reklam Metinleri</h3>
        <button onClick={()=>nav(`/ai-araclar/reklam-metni/yeni?customer_id=${cid}`)} className="ky-btn ky-btn-outline ky-btn-sm"><Sparkles size={11}/>Yeni Üret</button>
      </div>
      {sets.length === 0 && <div className="ky-card p-10 text-center text-sm text-[#94A3B8]">Henüz kayıtlı reklam metni yok. <button onClick={()=>nav("/ai-araclar/reklam-metni")} className="text-[#4F46E5] underline">Reklam Metni Üretici'den</button> üretip beğendiklerinizi seçerek kaydedin.</div>}
      {sets.map(s => (
        <div key={s.id} className="ky-card overflow-hidden">
          <button onClick={()=>setOpen({...open, [s.id]: !open[s.id]})} className="w-full p-3 flex items-center gap-3 text-left hover:bg-[#FAFAFB]">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{s.name}</div>
              <div className="text-[11px] text-[#94A3B8]">{(s.headlines||[]).length} başlık · {(s.descriptions||[]).length} açıklama · {new Date(s.created_at).toLocaleDateString("tr-TR")}</div>
            </div>
            <button onClick={(e)=>{e.stopPropagation(); remove(s.id);}} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={12}/></button>
          </button>
          {open[s.id] && (
            <div className="border-t border-[#ECECEF] p-3 space-y-3">
              {(s.headlines||[]).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#0F172A] mb-1.5">Başlıklar</div>
                  <div className="space-y-1">{s.headlines.map((h,i) => <div key={i} className="text-sm flex items-center gap-2"><span className="text-[10px] text-[#94A3B8] font-mono w-8">{(h.text||"").length}/30</span><span>{h.text}</span></div>)}</div>
                </div>
              )}
              {(s.descriptions||[]).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[#0F172A] mb-1.5">Açıklamalar</div>
                  <div className="space-y-1">{s.descriptions.map((d,i) => <div key={i} className="text-sm flex items-center gap-2"><span className="text-[10px] text-[#94A3B8] font-mono w-8 shrink-0">{(d.text||"").length}/90</span><span>{d.text}</span></div>)}</div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
